/**
 * CritAttackerFeedback — Attacker-side reward for landing a crit.
 *
 * The victim already has gold floaters / sparkles / hit-marker pulses (U4),
 * so the attacker side has no "you earned it" beat. This unit adds two
 * complementary feedback channels triggered when ANY of the local player's
 * drones lands a crit:
 *
 *   1. A 200ms rim-pulse on the attacker drone (ramp 50ms / hold 80ms /
 *      ramp 70ms = 200ms total). The pulse is delivered as a single
 *      multiplier number; the consumer (GameClient -> DroneRenderer)
 *      writes it into the existing `uRimStrength` uniform — no new mesh,
 *      no shader edits.
 *
 *   2. A small "✦" DOM floater above the attacker drone, parented to a
 *      dedicated child container so it never clashes with the victim-side
 *      damage floaters managed by DamageFloaters.ts.
 *
 * The class itself is DOM-aware (it manages floater elements) but the
 * gating + envelope helpers are exported separately so tests can assert
 * the contract without a jsdom environment.
 */
import * as THREE from 'three/webgpu';
import type { ClientNetManager } from '../networking/ClientNetManager';
import { ServerEvent } from '../../shared/types';
import type { Vec3 } from '../../shared/utils';

/* ── Tunables ─────────────────────────────────────────────────── */

/** Total pulse envelope duration in ms (50ms ramp-up + 80ms hold + 70ms ramp-down). */
export const RIM_PULSE_DURATION_MS = 200;
/** Time spent ramping up to peak. */
export const RIM_PULSE_RAMP_UP_MS = 50;
/** Time held at peak after ramp-up. */
export const RIM_PULSE_HOLD_MS = 80;
/** Time spent ramping back down to 1.0. */
export const RIM_PULSE_RAMP_DOWN_MS = 70;
/** Peak multiplier applied on top of the baseline `uRimStrength` (0.8). */
export const RIM_PULSE_PEAK_MULTIPLIER = 2.5;
/** Lifetime of the attacker-side "✦" floater. */
export const ATTACKER_FLOATER_LIFETIME_MS = 600;
/** Cap on simultaneous floaters to keep the DOM bounded under volley fire. */
const MAX_ACTIVE_FLOATERS = 16;

/* ── Pure helpers (DOM-free, unit-testable) ───────────────────── */

export interface CritGateState {
  localSessionId: string;
  localDroneIds: ReadonlySet<string>;
}

export interface CritEventData {
  isCrit?: unknown;
  attackerDroneId?: unknown;
}

/**
 * Decide whether a DamageDealt event should fire the attacker-side feedback.
 *
 * Truth table:
 *   - isCrit !== true            -> false (we ignore non-crit hits)
 *   - attackerDroneId not local  -> false (only OUR crits reward the player)
 *   - both                       -> true
 *
 * The attacker filter intentionally checks the *drone* id (not the player
 * id) so per-drone ownership is honored — admin mode spawns multiple local
 * drones and any of them can be the crit attacker.
 */
export function shouldTriggerCritAttackerFeedback(
  data: CritEventData,
  gateState: CritGateState,
): boolean {
  if (data?.isCrit !== true) return false;
  const attackerId = data?.attackerDroneId;
  if (typeof attackerId !== 'string') return false;
  return gateState.localDroneIds.has(attackerId);
}

/**
 * Sample the rim-pulse envelope at `elapsedMs` since pulse start. Returns
 * 1.0 (baseline) outside the active window, RIM_PULSE_PEAK_MULTIPLIER at
 * the apex, with linear ramps in/out so the eye reads a clean "flash".
 *
 * The envelope is deterministic — tests pin both edges and the apex.
 */
export function computeRimPulseMultiplier(elapsedMs: number): number {
  if (elapsedMs <= 0) return 1.0;
  if (elapsedMs >= RIM_PULSE_DURATION_MS) return 1.0;

  const peak = RIM_PULSE_PEAK_MULTIPLIER;
  const rampUpEnd = RIM_PULSE_RAMP_UP_MS;
  const holdEnd = RIM_PULSE_RAMP_UP_MS + RIM_PULSE_HOLD_MS;

  if (elapsedMs < rampUpEnd) {
    const t = elapsedMs / RIM_PULSE_RAMP_UP_MS;
    return 1.0 + (peak - 1.0) * t;
  }
  if (elapsedMs < holdEnd) {
    return peak;
  }
  // Ramp down: holdEnd .. RIM_PULSE_DURATION_MS.
  const t = (elapsedMs - holdEnd) / RIM_PULSE_RAMP_DOWN_MS;
  return peak + (1.0 - peak) * t;
}

/**
 * Combine an arbitrary number of overlapping pulse elapsed-times into a
 * single multiplier. Back-to-back crits should produce the *peak* of any
 * active pulse, not the sum — visually a held flash beats a stacked spike.
 *
 * Returns 1.0 (baseline) when no pulses are active.
 */
export function combineActivePulses(elapsedMsList: readonly number[]): number {
  let peak = 1.0;
  for (const elapsed of elapsedMsList) {
    const m = computeRimPulseMultiplier(elapsed);
    if (m > peak) peak = m;
  }
  return peak;
}

/* ── DOM-aware feedback driver ─────────────────────────────────── */

interface ActivePulse {
  elapsedMs: number;
}

export interface CritAttackerFeedbackDeps {
  /** Parent layer in the HUD; the class creates its own child container
   * inside this so the attacker-side floaters are *visually* separate
   * from the existing victim-side damage floaters. */
  layer: HTMLElement;
  /** Camera used to project drone world-space → screen pixels. */
  camera: THREE.Camera;
  /** Snapshot of the local player's drone-id set (and session id). Looked
   * up per-event because the local drone ids drift over a match (death,
   * respawn, server fan-out delay). */
  getGateState: () => CritGateState;
  /** Apply the combined rim multiplier to the drone material. The consumer
   * is expected to write into the global `uRimStrength` uniform; passing
   * a callback keeps this class testable without a WebGPU stack. */
  applyRimMultiplier: (multiplier: number) => void;
  /** Optional per-drone pulse for the actual crit attacker. */
  pulseAttackerDrone?: (droneId: string) => void;
}

export class CritAttackerFeedback {
  private readonly layer: HTMLElement;
  private readonly container: HTMLElement;
  private readonly camera: THREE.Camera;
  private readonly getGateState: () => CritGateState;
  private readonly applyRimMultiplier: (m: number) => void;
  private readonly pulseAttackerDrone?: (droneId: string) => void;
  private readonly projectVec = new THREE.Vector3();
  private readonly active: HTMLElement[] = [];
  private readonly pulses: ActivePulse[] = [];
  private attached = false;
  private currentMultiplier = 1.0;

  constructor(deps: CritAttackerFeedbackDeps) {
    this.layer = deps.layer;
    this.camera = deps.camera;
    this.getGateState = deps.getGateState;
    this.applyRimMultiplier = deps.applyRimMultiplier;
    this.pulseAttackerDrone = deps.pulseAttackerDrone;

    // Dedicated child container — keeps our floaters in the same HUD layer
    // as DamageFloaters but never lets the two pools collide on cleanup.
    this.container = document.createElement('div');
    this.container.id = 'crit-attacker-floaters';
    this.container.className = 'combat-feedback-layer';
    this.container.style.position = 'absolute';
    this.container.style.inset = '0';
    this.container.style.pointerEvents = 'none';
    this.layer.appendChild(this.container);
  }

  /** Subscribe to DamageDealt and self-route via {@link handleEvent}. */
  attach(net: ClientNetManager): void {
    if (this.attached) return;
    this.attached = true;
    net.onServerEvent((eventType, data) => {
      if (!this.attached) return;
      this.handleEvent(eventType, data);
    });
  }

  detach(): void {
    this.attached = false;
    for (const el of this.active) el.remove();
    this.active.length = 0;
    this.pulses.length = 0;
    this.applyRimMultiplier(1.0);
    this.currentMultiplier = 1.0;
  }

  /**
   * Handle a single server event. Filters to DamageDealt events that
   * meet the gating contract (see {@link shouldTriggerCritAttackerFeedback})
   * and spawns both the rim-pulse timer and the floater.
   *
   * Public so tests + the GameClient inline block can both call it.
   */
  handleEvent(eventType: string, data: Record<string, unknown>): void {
    if (eventType !== ServerEvent.DamageDealt) return;
    const gate = this.getGateState();
    if (!shouldTriggerCritAttackerFeedback(data as CritEventData, gate)) return;

    // Prefer the attacker world position from the event payload (server
    // attaches it for the projectile visualizer). If absent, skip the
    // floater but still flash the rim — the rim is the primary cue.
    const attackerPos = data.attackerPosition as Vec3 | undefined;
    const attackerId = data.attackerDroneId as string;
    this.startPulse();
    this.pulseAttackerDrone?.(attackerId);
    if (attackerPos) {
      this.spawnAttackerFloater(attackerPos);
    }
  }

  /** Drive active pulses forward and emit the new combined multiplier.
   * Folds advance + prune + peak into a single pass so an active pulse
   * costs ~3 numeric ops per frame and zero array allocations. */
  update(deltaMs: number): void {
    if (this.pulses.length === 0) {
      if (this.currentMultiplier !== 1.0) {
        this.currentMultiplier = 1.0;
        this.applyRimMultiplier(1.0);
      }
      return;
    }

    let peak = 1.0;
    for (let i = this.pulses.length - 1; i >= 0; i--) {
      const next = this.pulses[i].elapsedMs + deltaMs;
      if (next >= RIM_PULSE_DURATION_MS) {
        this.pulses.splice(i, 1);
        continue;
      }
      this.pulses[i].elapsedMs = next;
      const m = computeRimPulseMultiplier(next);
      if (m > peak) peak = m;
    }

    if (peak !== this.currentMultiplier) {
      this.currentMultiplier = peak;
      this.applyRimMultiplier(peak);
    }
  }

  /** Queue a fresh rim pulse. Multiple stacked pulses combine via peak. */
  private startPulse(): void {
    this.pulses.push({ elapsedMs: 0 });
  }

  /** Spawn a single attacker-side "✦" floater at the drone's projected
   * screen position. Mirrors the DamageFloaters pattern (CSS-driven
   * animation, JS fallback cleanup) so the lifecycle matches. */
  private spawnAttackerFloater(world: Vec3): void {
    const screen = this.worldToScreen(world);
    if (!screen) return;
    if (this.active.length >= MAX_ACTIVE_FLOATERS) {
      this.active.shift()?.remove();
    }
    const el = document.createElement('div');
    el.className = 'crit-attacker-floater';
    el.textContent = '✦';
    el.style.position = 'absolute';
    el.style.left = `${screen.x}px`;
    el.style.top = `${screen.y}px`;
    this.container.appendChild(el);
    this.active.push(el);

    let removed = false;
    const cleanup = () => {
      if (removed) return;
      removed = true;
      el.removeEventListener('animationend', cleanup);
      const idx = this.active.indexOf(el);
      if (idx >= 0) this.active.splice(idx, 1);
      el.remove();
    };
    el.addEventListener('animationend', cleanup);
    setTimeout(cleanup, ATTACKER_FLOATER_LIFETIME_MS + 200);
  }

  /** Project a world point to layer-relative pixels. Returns null when
   * the point is behind the camera. */
  private worldToScreen(world: Vec3): { x: number; y: number } | null {
    this.projectVec.set(world.x, world.y, world.z).project(this.camera);
    if (this.projectVec.z > 1) return null;
    const w = this.layer.clientWidth || (typeof window !== 'undefined' ? window.innerWidth : 1920);
    const h = this.layer.clientHeight || (typeof window !== 'undefined' ? window.innerHeight : 1080);
    return {
      x: (this.projectVec.x * 0.5 + 0.5) * w,
      y: (-this.projectVec.y * 0.5 + 0.5) * h,
    };
  }
}
